// src/schemas.ts
import { z } from "zod";
var idSchema = z.uuid();
var localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
var loginRequestSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(12).max(256),
  csrfToken: z.string().min(32).max(256).optional()
});
var onboardingSchema = z.object({
  displayName: z.string().trim().min(1).max(80).default("Rahul"),
  heightCm: z.number().min(100).max(260),
  metabolicSex: z.enum(["female", "male"]),
  activityLevel: z.enum(["mostly_seated", "lightly_active", "active", "very_active"]),
  desiredProgressRate: z.enum(["slow", "moderate"]),
  dietaryPreference: z.string().trim().max(120),
  foodsAvoided: z.array(z.string().trim().min(1).max(100)).max(50),
  sensitivities: z.array(z.string().trim().min(1).max(160)).max(50),
  cookingPreference: z.string().trim().max(200),
  budgetPreference: z.string().trim().max(200),
  gymWeekdays: z.array(z.number().int().min(0).max(6)).length(3),
  kickboxingWeekday: z.number().int().min(0).max(6),
  healthNotes: z.string().trim().max(2e3).optional(),
  healthConnectDeferred: z.boolean(),
  progressPhotosDeferred: z.boolean()
}).superRefine((input, context) => {
  if (new Set(input.gymWeekdays).size !== input.gymWeekdays.length) {
    context.addIssue({
      code: "custom",
      path: ["gymWeekdays"],
      message: "Choose three different gym days."
    });
  }
  if (input.gymWeekdays.includes(input.kickboxingWeekday)) {
    context.addIssue({
      code: "custom",
      path: ["gymWeekdays"],
      message: "Choose gym days that are different from kickboxing day."
    });
  }
});
var energyCheckInSchema = z.object({
  localDate: localDateSchema,
  energy: z.number().int().min(1).max(5),
  soreness: z.number().int().min(1).max(5),
  mood: z.number().int().min(1).max(5),
  stress: z.number().int().min(1).max(5),
  asthmaSymptoms: z.boolean()
});
var createWorkoutSchema = z.object({
  clientId: idSchema,
  routineVersionId: idSchema,
  startedAt: z.iso.datetime({ offset: true }),
  timezone: z.string().min(1).max(80),
  exerciseInstances: z.array(
    z.object({
      routineExerciseId: idSchema,
      workoutExerciseId: idSchema
    })
  ).max(50).default([]).refine(
    (items) => new Set(items.map((item) => item.routineExerciseId)).size === items.length,
    "Routine exercise IDs must be unique."
  ).refine(
    (items) => new Set(items.map((item) => item.workoutExerciseId)).size === items.length,
    "Workout exercise IDs must be unique."
  )
});
var workoutSetSchema = z.object({
  clientId: idSchema,
  workoutExerciseId: idSchema,
  setNumber: z.number().int().positive().max(100),
  setType: z.enum(["warmup", "working"]),
  weightKg: z.number().nonnegative().max(1e3),
  reps: z.number().int().nonnegative().max(1e3),
  rir: z.number().int().min(0).max(10).nullable(),
  formQuality: z.enum(["good", "mixed", "poor"]),
  pain: z.boolean(),
  completedAt: z.iso.datetime({ offset: true })
});
var completeWorkoutSchema = z.object({
  endedAt: z.iso.datetime({ offset: true }),
  notes: z.string().trim().max(5e3).default(""),
  perceivedEffort: z.number().int().min(1).max(5).nullable()
});
var weightEntrySchema = z.object({
  clientId: idSchema,
  measuredAt: z.iso.datetime({ offset: true }),
  weightKg: z.number().positive().max(500),
  source: z.enum(["manual", "health_connect", "fitbit"]),
  notes: z.string().trim().max(1e3).default("")
});
var waterEntrySchema = z.object({
  clientId: idSchema,
  occurredAt: z.iso.datetime({ offset: true }),
  amountMl: z.number().int().positive().max(1e4)
});
var foodDiaryEntrySchema = z.object({
  clientId: idSchema,
  localDate: localDateSchema,
  mealSlot: z.string().trim().min(1).max(80),
  foodId: idSchema,
  servingId: idSchema.nullable().default(null),
  amount: z.number().positive().max(1e5),
  unit: z.enum(["g", "ml", "serving"]),
  occurredAt: z.iso.datetime({ offset: true }),
  calculatedNutrition: z.object({
    caloriesKcal: z.number().nonnegative().max(1e5),
    proteinG: z.number().nonnegative().max(1e4),
    carbohydrateG: z.number().nonnegative().max(1e4),
    fatG: z.number().nonnegative().max(1e4),
    fibreG: z.number().nonnegative().max(1e4)
  })
});
var customFoodSchema = z.object({
  name: z.string().trim().min(1).max(240),
  brand: z.string().trim().max(160).nullable().default(null),
  barcode: z.string().trim().max(40).nullable().default(null),
  basisGrams: z.number().positive().max(1e5).default(100),
  nutritionPerBasis: z.object({
    caloriesKcal: z.number().nonnegative().max(1e5),
    proteinG: z.number().nonnegative().max(1e4),
    carbohydrateG: z.number().nonnegative().max(1e4),
    fatG: z.number().nonnegative().max(1e4),
    fibreG: z.number().nonnegative().max(1e4)
  })
});
var photoMealConfirmationSchema = z.object({
  clientId: idSchema,
  localDate: localDateSchema,
  mealSlot: z.string().trim().min(1).max(80),
  occurredAt: z.iso.datetime({ offset: true }),
  items: z.array(z.object({
    name: z.string().trim().min(1).max(160),
    estimatedGrams: z.number().positive().max(1e4),
    nutrition: z.object({
      caloriesKcal: z.number().nonnegative().max(1e5),
      proteinG: z.number().nonnegative().max(1e4),
      carbohydrateG: z.number().nonnegative().max(1e4),
      fatG: z.number().nonnegative().max(1e4),
      fibreG: z.number().nonnegative().max(1e4)
    })
  })).min(1).max(20)
});
var journalEntrySchema = z.object({
  clientId: idSchema,
  localDate: localDateSchema,
  morning: z.record(z.string(), z.unknown()).default({}),
  evening: z.record(z.string(), z.unknown()).default({}),
  bodyMarkdown: z.string().max(1e5).default(""),
  tags: z.array(z.string().trim().min(1).max(50)).max(30).default([]),
  pinned: z.boolean().default(false)
});
var healthIngestRecordSchema = z.object({
  provider: z.literal("health_connect"),
  recordType: z.string().min(1).max(120),
  externalRecordId: z.string().min(1).max(500),
  clientRecordId: z.string().max(500).nullable(),
  dataOriginPackage: z.string().min(1).max(300),
  recordStart: z.iso.datetime({ offset: true }),
  recordEnd: z.iso.datetime({ offset: true }),
  startOffsetMinutes: z.number().int().min(-1080).max(1080).nullable(),
  endOffsetMinutes: z.number().int().min(-1080).max(1080).nullable(),
  metric: z.string().min(1).max(80),
  value: z.number(),
  unit: z.string().min(1).max(40),
  sourceCreatedAt: z.iso.datetime({ offset: true }).nullable().default(null),
  sourceUpdatedAt: z.iso.datetime({ offset: true }).nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({})
});
var healthIngestSchema = z.object({
  records: z.array(healthIngestRecordSchema).max(1e3),
  deletedExternalIds: z.array(z.string().min(1).max(500)).max(1e3).default([]),
  cursor: z.object({
    recordType: z.string().min(1).max(120),
    token: z.string().min(1).max(2e4)
  })
});
export {
  completeWorkoutSchema,
  createWorkoutSchema,
  customFoodSchema,
  energyCheckInSchema,
  foodDiaryEntrySchema,
  healthIngestRecordSchema,
  healthIngestSchema,
  idSchema,
  journalEntrySchema,
  localDateSchema,
  loginRequestSchema,
  onboardingSchema,
  photoMealConfirmationSchema,
  waterEntrySchema,
  weightEntrySchema,
  workoutSetSchema
};
